import { apiClient } from "@/api/client";
import type { ChatRequest, ChatResponse } from "@/domains/chat/types";
import { useMutation } from "@tanstack/react-query";
import { captureEvent } from "@/analytics/posthog";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const normalizeEmbeddedResponse = (response: ChatResponse): ChatResponse => {
  const trimmedAnswer = response.answer.trim();
  const unfencedAnswer = trimmedAnswer
    .replace(/^```(?:json)?\s*/iu, "")
    .replace(/\s*```$/u, "")
    .trim();

  if (!unfencedAnswer.startsWith("{") || !unfencedAnswer.endsWith("}")) {
    return response;
  }

  try {
    const parsed: unknown = JSON.parse(unfencedAnswer);
    if (!isRecord(parsed) || typeof parsed.answer !== "string") return response;

    const recommendations = Array.isArray(parsed.recommendations)
      ? parsed.recommendations.filter(
          (item): item is { name: string; amount: number; reason: string } =>
            isRecord(item) &&
            typeof item.name === "string" &&
            typeof item.amount === "number" &&
            typeof item.reason === "string",
        )
      : [];

    return {
      answer: parsed.answer,
      has_recommendations:
        typeof parsed.has_recommendations === "boolean"
          ? parsed.has_recommendations && recommendations.length > 0
          : recommendations.length > 0,
      recommendations,
      summary: typeof parsed.summary === "string" ? parsed.summary : null,
    };
  } catch {
    return response;
  }
};

export const useAskDomainChat = () =>
  useMutation({
    mutationFn: async (request: ChatRequest) => {
      const response = await apiClient.post<ChatResponse>("/chat/ask", request);
      return normalizeEmbeddedResponse(response.data);
    },
    onSuccess: (_response, request) => {
      captureEvent("chat_question_completed", { domain: request.domain });
    },
  });
